/**
 * Errors raised by the OAuth token endpoint, classified by recoverability.
 *
 * GH #1538: a `400 invalid_grant` from the Cognito token endpoint means the
 * stored refresh token is expired/revoked — retrying can never succeed, so the
 * only recovery is to re-authenticate. Everything else (network blips, 5xx,
 * throttling) is transient and must NOT log the user out — that would discard a
 * still-valid session (the GH #1469 regression).
 */
export class AuthTokenError extends Error {
  readonly status: number
  readonly oauthError?: string
  /** `true` when retrying cannot succeed and the user must re-authenticate. */
  readonly permanent: boolean

  constructor(status: number, oauthError?: string) {
    super(`Authentication failed: ${oauthError ?? status}`)
    this.name = 'AuthTokenError'
    this.status = status
    this.oauthError = oauthError
    this.permanent = status === 400 && oauthError === 'invalid_grant'
  }
}

/** `true` when an error from the token endpoint cannot be recovered by retrying. */
export function isPermanentAuthError(error: unknown): error is AuthTokenError {
  return error instanceof AuthTokenError && error.permanent
}
